import ActionStrip from 'components/ModeStrip/Strip';
import TreeRedoButton from 'components/Tree/RedoBtn';
import TreeUndoButton from 'components/Tree/UndoBtn';
import type { CSSProperties } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { setIsDrawingTree, setSelectedShapeId, toggleDeleteTreesMode, toggleFlattenAreaMode } from 'store/slices/TreeSlice';
import { selectIsFlattenAreaMode, selectTreeDeleteMode } from 'store/slices/TreeSlice/selector';
import { TreeEditModeToggle, TreewithFlatten } from './styles';
import FlattenShapeStrip from 'components/FlattenArea/FlattenShapeStrip';
import { selectTreeDoneLoading, selectIsFlattenLoading } from 'store/slices/TreeSlice/selector';

type TreeEditAction = 'add' | 'delete' | 'flatten';
const TreeEditActions: Array<{ label: string; val: TreeEditAction }> = [
	{ label: 'Add', val: 'add' },
	{ label: 'Remove', val: 'flatten' },
	{ label: 'Delete', val: 'delete' },
];
const TreeEditModeByAction: Record<TreeEditAction, { deleteMode: boolean; flattenMode: boolean }> = {
	add: { deleteMode: false, flattenMode: false },
	delete: { deleteMode: true, flattenMode: false },
	flatten: { deleteMode: false, flattenMode: true },
};

export default function TreeEditingModeStrip() {
	const dispatch = useDispatch();
	const deleteMode = useSelector(selectTreeDeleteMode);
	const isFlattenAreaMode = useSelector(selectIsFlattenAreaMode);
	const draftFlattenPointsCount = useSelector((state: RootState) => state.treeSlice.data.draftFlatten.points.length);
	const isDoneLoading = useSelector(selectTreeDoneLoading);
	const isFlattenLoading = useSelector(selectIsFlattenLoading);
	const activeAction: TreeEditAction = isFlattenAreaMode ? 'flatten' : (deleteMode ? 'delete' : 'add');
	const activeIndex = TreeEditActions.findIndex((a) => a.val === activeAction);
	const flattenDrawingInProgress = isFlattenAreaMode && draftFlattenPointsCount > 0;

	const selectAction = (action: TreeEditAction) => {
		if (isDoneLoading) return;
		if (action === activeAction) return;
		if (flattenDrawingInProgress && action !== 'flatten') return;

		// Reset selection on any mode switch for a clean UX.
		dispatch(setSelectedShapeId(null));
		dispatch(setIsDrawingTree(false));

		const nextMode = TreeEditModeByAction[action];
		dispatch(toggleDeleteTreesMode(nextMode.deleteMode));
		dispatch(toggleFlattenAreaMode(nextMode.flattenMode));
	};

	return (
		<ActionStrip
			cancelDisabled={isDoneLoading}
			action="Edit Trees"
			icon={<svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 24 24" fill="none">
				<g clipPath="url(#clip0_3909_8)">
					<path d="M10.3615 22.9575V8.83367M13.5434 17.5253H14.604C15.9191 17.5264 
					17.1876 17.0269 18.1634 16.1239C19.1392 15.2209 19.7527 13.9787 19.8847 
					12.6384C20.0167 11.2982 19.6579 9.95548 18.8779 8.87096C18.0979 7.78645 
					16.9524 7.03747 15.6636 6.76941C15.6636 3.76864 13.5434 1.22852 10.3615 
					1.22852C7.17954 1.22852 5.05932 3.76864 5.05932 6.76941C3.77459 7.04201 2.63405 
					7.79255 1.85793 8.8761C1.08181 9.95966 0.725173 11.2994 0.856897 12.6365C0.98862
					13.9736 1.59936 15.2133 2.57116 16.1162C3.54297 17.0191 4.80691 17.5211 6.1189
					17.5253H7.17954M10.3615 15.3524L13.0131 12.6362M10.3615 13.1795L7.70986 10.4633M8.24018
					22.9575H12.4827"
					stroke='var(--defaultIconColor)'
					strokeWidth="1.37" strokeLinecap="round" strokeLinejoin="round" />
					<path d="M10.3615 22.9575V8.83367M13.5434 17.5253H14.604C15.9191 17.5264 17.1876 17.0269 
					18.1634 16.1239C19.1392 15.2209 19.7527 13.9787 19.8847 12.6384C20.0167 11.2982 19.6579 
					9.95548 18.8779 8.87096C18.0979 7.78645 16.9524 7.03747 15.6636 6.76941C15.6636 3.76864 
					13.5434 1.22852 10.3615 1.22852C7.17954 1.22852 5.05932 3.76864 5.05932 6.76941C3.77459 
					7.04201 2.63405 7.79255 1.85793 8.8761C1.08181 9.95966 0.725173 11.2994 0.856897 
					12.6365C0.98862 13.9736 1.59936 15.2133 2.57116 16.1162C3.54297 17.0191 4.80691 17.5211 
					6.1189 17.5253H7.17954M10.3615 15.3524L13.0131 12.6362M10.3615 13.1795L7.70986 
					10.4633M8.24018 22.9575H12.4827"
					stroke='var(--defaultIconColor)' strokeWidth="1.37" strokeLinecap="round" strokeLinejoin="round" />
					<path d="M20.4565 17.6736L22.0835 19.3402M18.2872 23.2289L22.8346 18.5541C23.0482 
					18.3353 23.1682 18.0386 23.1682 17.7292C23.1682 17.4198 23.0482 17.123 22.8346 
					16.9042C22.621 16.6854 22.3313 16.5625 22.0293 16.5625C21.7272 16.5625 21.4375 16.6854 
					21.2239 16.9042L16.6602 21.5623V23.2289H18.2872Z"
					stroke='var(--defaultIconColor)'
					strokeLinecap="round" strokeLinejoin="round" />
				</g>
				<defs>
					<clipPath id="clip0_3909_8">
						<rect width="24" height="24" fill="white" />
					</clipPath>
				</defs>
			</svg>}
		>
			<TreewithFlatten>
				<TreeEditModeToggle
					data-index={activeIndex}
					style={{ '--index': String(Math.max(activeIndex, 0)) } as CSSProperties}
				>
					<div className="slider" />

					{TreeEditActions.filter((action) => action.val !== 'delete').map((action) => (
						<label
							key={action.val}
							className="seg-control tree-flatten-control"
							data-disabled={(flattenDrawingInProgress && action.val !== 'flatten') || isDoneLoading ? 'true' : 'false'}
						>
							<input
								id={`tree-edit-action-${action.val}`}
								type="radio"
								name="tree-edit-action"
								checked={action.val === activeAction}
								onChange={() => selectAction(action.val)}
								disabled={(flattenDrawingInProgress && action.val !== 'flatten') || isDoneLoading}
							/>
							<span className="seg-label">{action.label}</span>
						</label>
					))}
				</TreeEditModeToggle>
				{isFlattenAreaMode && <FlattenShapeStrip />}
			</TreewithFlatten>

			<TreeUndoButton disabled={isDoneLoading || isFlattenLoading} />
			<TreeRedoButton disabled={isDoneLoading || isFlattenLoading} />
		</ActionStrip>
	);
}
